import { CustomTooltip } from '@/components/custom-tooltip';
import type { ICellRendererParams } from 'ag-grid-community';
import {
	type RestrictionType,
	getRestrictionConfig,
	parseRestrictionCode,
} from './restriction-helpers';
import { formatRecAction } from './tableCustomCellRender';
import type { RecReviewData } from './useDashboardController';

interface RestrictionCellProps extends ICellRendererParams<RecReviewData> {
	data: RecReviewData;
}

interface RestrictionIconProps {
	restrictionType: RestrictionType;
	color: string;
	code: string;
	label: string;
	details?: string;
	data: RecReviewData;
	nights?: number;
}

const getRestrictionIconProps = (
	code: string,
	data: RecReviewData,
): RestrictionIconProps => {
	// Parse the restriction code to get both type and nights
	const { type: restrictionType, nights } = parseRestrictionCode(code);

	// Get config with nights parameter for MLOS restrictions
	const config = getRestrictionConfig(restrictionType, nights);

	return {
		restrictionType,
		color: config.color,
		code,
		label: config.label,
		data,
		nights,
	};
};

const RestrictionTooltip = (props: {
	restrictionType: RestrictionType;
	label: string;
	code: string;
	data: RecReviewData;
}): JSX.Element => {
	const recAction = (props.data as RecReviewData & { a_rec_action?: string })
		?.a_rec_action;

	return (
		<div className='flex flex-col space-y-1'>
			<div
				className='font-bold text-gray-800 border-b pb-1 mb-1'
				style={{ fontSize: '.9rem' }}
			>
				Rate Level A
			</div>
			<div>Restriction: {recAction ? formatRecAction(recAction) : '-'}</div>
			<div>Status: {props.code}</div>
			<div>Applies to: All room types</div>
		</div>
	);
};

const RestrictionIcon = (props: RestrictionIconProps): JSX.Element => {
	const { color, restrictionType, label, code, data } = props;

	return (
		<CustomTooltip
			content={
				<RestrictionTooltip
					restrictionType={restrictionType}
					label={label}
					code={code}
					data={data}
				/>
			}
			placement='top'
			delayDuration={300}
			backgroundColor='tw-bg-white'
			textColor='!tw-text-black'
			borderColor='tw-border-primary'
		>
			<div
				className='w-3 h-3'
				style={{
					backgroundColor: color,
					width: '12px',
					height: '12px',
					borderRadius: '2px',
				}}
			/>
		</CustomTooltip>
	);
};

export const RestrictionCell = (props: RestrictionCellProps): JSX.Element => {
	const { data } = props;

	const restrictionCode: string =
		(data as RecReviewData & { a_status?: string })?.a_status || 'none';

	const iconProps = getRestrictionIconProps(restrictionCode, data);
	const details =
		restrictionCode && restrictionCode !== 'none'
			? `Rate Level A restriction: ${restrictionCode}`
			: undefined;

	return (
		<div className='flex items-center justify-center h-full p-1'>
			<RestrictionIcon {...iconProps} details={details} />
		</div>
	);
};
